import { IconSize, heightForIconSize } from "@components/Icon";
import Image, { ImageProps } from "next/image";
import React from "react";

import {
  srcsetObjectForCloudinaryId,
  ConstructCloudinaryUrlParams,
  ImageFormat,
  Dimensions,
} from "@every.org/common/src/helpers/cloudinary";

export interface CloudinaryImgProps
  extends Omit<ImageProps, "alt" | "src" | "fill" | "width" | "height">,
    Omit<ConstructCloudinaryUrlParams, "dimensions"> {
  objectCover?: boolean;
  /** @deprecated layout is ignored; fill is determined by whether dimensions are provided */
  layout?: "fill" | "responsive" | "fixed" | "intrinsic";
  dimensions?: ConstructCloudinaryUrlParams["dimensions"];
  priority?: ImageProps["priority"];
  alt?: string;
  iconSize?: IconSize;
}

/**
 *
 * @see ConstructCloudinaryUrlParams for details on how to customize the output
 */
export const CloudinaryImg = React.memo(function CloudinaryImgImpl({
  cloudinaryId,
  applySizeAfterTransforms,
  transforms,
  dimensions,
  imageFormat,
  fillMode,
  objectCover = true,
  sizes,
  layout,
  priority,
  alt = "",
  iconSize,
  style,
  ...rest
}: CloudinaryImgProps) {
  const dimensionsObj =
    iconSize && !dimensions
      ? {
          height: heightForIconSize[iconSize],
          width: heightForIconSize[iconSize],
        }
      : dimensions;

  const isFill =
    layout === "fill" || !dimensionsObj?.width || !dimensionsObj?.height;

  const imgSrc =
    imageFormat === ImageFormat.svg
      ? srcsetObjectForCloudinaryId({
          cloudinaryId,
          applySizeAfterTransforms,
          dimensions: dimensionsObj as Dimensions,
          imageFormat,
          fillMode,
        })["1x"]
      : cloudinaryId.startsWith("/")
      ? cloudinaryId
      : `/${cloudinaryId}`;

  const sharedProps = {
    ...rest,
    src: imgSrc,
    style: {
      ...style,
      objectFit: (objectCover
        ? "cover"
        : "contain") as React.CSSProperties["objectFit"],
    },
    sizes,
    unoptimized: imageFormat === ImageFormat.svg,
    priority,
    alt,
  };

  if (isFill) {
    return (
      <span
        style={{
          position: "relative",
          display: "block",
          width: "100%",
          height: "100%",
        }}
      >
        <Image {...sharedProps} fill />
      </span>
    );
  }

  return (
    <Image
      {...sharedProps}
      width={dimensionsObj!.width}
      height={dimensionsObj!.height}
      style={{
        width: "auto",
        height: "auto",
        ...sharedProps.style,
      }}
    />
  );
});
